"use client"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom" // Thêm useNavigate
import "./ProductManager.css"
import productService from "../../../services/api/productService"
import productCategoryService from "../../../services/api/productCategoryService"

export default function ProductManagement() {
    const navigate = useNavigate() // Khởi tạo useNavigate
    // State for products list
    const [products, setProducts] = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    // State for form inputs
    const [formData, setFormData] = useState({
        productname: "",
        photo_title: "/placeholder.svg?height=100&width=300",
        product_img: [],
        product_category: "",
    })

    // State for views
    const [showAddForm, setShowAddForm] = useState(false)

    // Fetch products and categories on component mount
    useEffect(() => {
        fetchProducts()
        fetchCategories()
    }, [])

    // Fetch all categories
    const fetchCategories = async () => {
        try {
            const response = await productCategoryService.getAllCategories()
            setCategories(Array.isArray(response) ? response : [])
        } catch (error) {
            console.error("Lỗi khi tải danh mục:", error)
            setError("Không thể tải danh mục sản phẩm")
        }
    }

    // Fetch all products
    const fetchProducts = async () => {
        try {
            setLoading(true)
            const response = await productService.getAllProducts()
            console.log('Phản hồi API:', response)
            setProducts(Array.isArray(response) ? response : [])
        } catch (error) {
            console.error("Lỗi khi tải danh sách sản phẩm:", error)
            setError("Không thể tải danh sách sản phẩm")
            setProducts([])
        } finally {
            setLoading(false)
        }
    }

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData({
            ...formData,
            [name]: value,
        })
    }

    // Handle image upload
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files)
            const newImages = []

            files.forEach(file => {
                const reader = new FileReader()
                reader.onload = (event) => {
                    if (event.target && event.target.result) {
                        // Compress image before setting state
                        const img = new Image()
                        img.src = event.target.result
                        img.onload = () => {
                            const canvas = document.createElement('canvas')
                            const MAX_WIDTH = 800
                            const MAX_HEIGHT = 800
                            let width = img.width
                            let height = img.height

                            if (width > height) {
                                if (width > MAX_WIDTH) {
                                    height *= MAX_WIDTH / width
                                    width = MAX_WIDTH
                                }
                            } else {
                                if (height > MAX_HEIGHT) {
                                    width *= MAX_HEIGHT / height
                                    height = MAX_HEIGHT
                                }
                            }

                            canvas.width = width
                            canvas.height = height
                            const ctx = canvas.getContext('2d')
                            ctx.drawImage(img, 0, 0, width, height)

                            const compressedImage = canvas.toDataURL('image/jpeg', 0.7)
                            newImages.push(compressedImage)

                            // Update state when all images are processed
                            if (newImages.length === files.length) {
                                setFormData(prev => ({
                                    ...prev,
                                    product_img: [...prev.product_img, ...newImages]
                                }))
                            }
                        }
                    }
                }
                reader.readAsDataURL(file)
            })
        }
    }

    // Handle remove image
    const handleRemoveImage = (index) => {
        setFormData(prev => ({
            ...prev,
            product_img: prev.product_img.filter((_, i) => i !== index)
        }))
    }

    // Handle title image upload
    const handleTitleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    // Compress image before setting state
                    const img = new Image()
                    img.src = event.target.result
                    img.onload = () => {
                        const canvas = document.createElement('canvas')
                        const MAX_WIDTH = 800
                        const MAX_HEIGHT = 800
                        let width = img.width
                        let height = img.height

                        if (width > height) {
                            if (width > MAX_WIDTH) {
                                height *= MAX_WIDTH / width
                                width = MAX_WIDTH
                            }
                        } else {
                            if (height > MAX_HEIGHT) {
                                width *= MAX_HEIGHT / height
                                height = MAX_HEIGHT
                            }
                        }

                        canvas.width = width
                        canvas.height = height
                        const ctx = canvas.getContext('2d')
                        ctx.drawImage(img, 0, 0, width, height)

                        const compressedImage = canvas.toDataURL('image/jpeg', 0.7)
                        setFormData({
                            ...formData,
                            photo_title: compressedImage,
                        })
                    }
                }
            }
            reader.readAsDataURL(file)
        }
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            await productService.createProduct(formData)
            await fetchProducts()
            // Reset form
            setFormData({
                productname: "",
                photo_title: "/placeholder.svg?height=100&width=300",
                product_img: [],
                product_category: "",
            })
            // Hide the form after submission
            setShowAddForm(false)
        } catch (error) {
            console.error("Lỗi khi tạo sản phẩm mới:", error)
            setError("Không thể tạo sản phẩm mới")
        } finally {
            setLoading(false)
        }
    }

    // Handle product deletion
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa sản phẩm này không?")) {
            try {
                setLoading(true)
                await productService.deleteProduct(id)
                await fetchProducts()
            } catch (error) {
                console.error("Lỗi khi xóa sản phẩm:", error)
                setError("Không thể xóa sản phẩm")
            } finally {
                setLoading(false)
            }
        }
    }

    // Handle view details
    const handleViewDetails = (product) => {
        navigate(`/manager/products/detail/${product._id}`) // Điều hướng đến route chi tiết
    }

    // Toggle add form
    const toggleAddForm = () => {
        setShowAddForm(!showAddForm)
    }

    return (
        <div className="pm-container">
            <h1 className="pm-heading">Quản Lý Sản Phẩm</h1>
            {error && <div className="error-message">{error}</div>}
            {loading && <div className="loading-spinner">Đang tải...</div>}
            <div className="pm-layout">
                <div className="pm-list-container">
                    <div className="pm-list-header">
                        <h2 className="pm-subheading">Danh Sách Sản Phẩm</h2>
                        <button className="pm-add-button" onClick={toggleAddForm} disabled={loading}>
                            {showAddForm ? "Ẩn Form" : "Thêm Sản Phẩm Mới"}
                        </button>
                    </div>
                    <div className="pm-list">
                        {Array.isArray(products) && products.length > 0 ? (
                            products.map((product) => (
                                <div key={product._id} className="pm-card">
                                    <div className="pm-title-image">
                                        <img src={product.photo_title || "/placeholder.svg"} alt="Ảnh tiêu đề" />
                                    </div>
                                    <div className="pm-details">
                                        <h3>{product.productname}</h3>
                                        <p>
                                            <strong>Danh mục:</strong> {product.product_category?.name || "Chưa phân loại"}
                                        </p>
                                        <div className="pm-actions">
                                            <button className="pm-view-button" onClick={() => handleViewDetails(product)}>
                                                Xem Chi Tiết
                                            </button>
                                            <button className="pm-delete-button" onClick={() => handleDelete(product._id)}>
                                                Xóa
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-products">Không tìm thấy sản phẩm nào</div>
                        )}
                    </div>
                </div>

                {showAddForm && (
                    <div className="pm-add-section">
                        <div className="pm-add-card">
                            <h2 className="pm-subheading">Thêm Sản Phẩm Mới</h2>
                            <form onSubmit={handleSubmit} className="pm-form">
                                <div className="pm-form-group">
                                    <label htmlFor="productname">Tên Sản Phẩm</label>
                                    <input
                                        type="text"
                                        id="productname"
                                        name="productname"
                                        value={formData.productname}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="pm-form-group">
                                    <label htmlFor="product_category">Danh Mục</label>
                                    <select
                                        id="product_category"
                                        name="product_category"
                                        value={formData.product_category}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Chọn danh mục</option>
                                        {categories.map((category) => (
                                            <option key={category._id} value={category._id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="pm-form-group">
                                    <label htmlFor="photo_title">Ảnh Tiêu Đề</label>
                                    <input
                                        type="file"
                                        id="photo_title"
                                        name="photo_title"
                                        onChange={handleTitleImageChange}
                                        accept="image/*"
                                    />
                                    <div className="pm-image-preview">
                                        <img src={formData.photo_title || "/placeholder.svg"} alt="Xem trước ảnh tiêu đề" />
                                        <p>Ảnh này sẽ hiển thị trong danh sách sản phẩm</p>
                                    </div>
                                </div>

                                <div className="pm-form-group">
                                    <label htmlFor="product_img">Ảnh Sản Phẩm</label>
                                    <input
                                        type="file"
                                        id="product_img"
                                        name="product_img"
                                        onChange={handleImageChange}
                                        accept="image/*"
                                        multiple
                                    />
                                    <div className="pm-images-preview">
                                        {formData.product_img.map((img, index) => (
                                            <div key={index} className="pm-image-preview-item">
                                                <img src={img} alt={`Preview ${index + 1}`} />
                                                <button
                                                    type="button"
                                                    className="pm-remove-image"
                                                    onClick={() => handleRemoveImage(index)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        {formData.product_img.length === 0 && (
                                            <div className="pm-image-preview-placeholder">
                                                <img src="/placeholder.svg" alt="No images" />
                                                <p>Chưa có ảnh nào được chọn</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="pm-form-actions">
                                    <button type="button" className="pm-cancel-button" onClick={toggleAddForm}>
                                        Hủy
                                    </button>
                                    <button type="submit" className="pm-submit-button" disabled={loading}>
                                        {loading ? "Đang thêm..." : "Thêm Sản Phẩm"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}